import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LoadingState } from '../ui/LoadingState.js';
import { KVGrid } from '../ui/KVGrid.js';
import { KVRow } from '../ui/KVRow.js';
import { Field } from '../ui/Field.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { Dropdown } from '../ui/Dropdown.js';
import { Select } from '../ui/Select.js';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';
import { Tabs } from '../ui/Tabs.js';
import { Icon } from '../Icon.js';
import { type PillTone } from '../PillGroup.js';
import { formatMoney } from '../../lib/money.js';
import { previewTotals } from '../../lib/billingTotals.js';
import { useContactProjects } from '../../hooks/useProjects.js';
import {
  useEstimate,
  useCreateEstimate,
  useUpdateEstimate,
  useInvoices,
  type EstimateLineItem,
  type BillingAccount,
} from '../../hooks/useBilling.js';
import { EstimateOutreachTab } from './EstimateOutreachTab.js';

type DetailTab = 'info' | 'outreach';

export const ESTIMATE_STATUS_TONES: Record<string, PillTone> = {
  draft: 'neutral',
  sent: 'cyan',
  accepted: 'success',
  declined: 'danger',
  expired: 'muted',
  converted: 'info',
  // Invoice statuses — same map, keyed by status string (BillingPage list +
  // DetailHeader). 'overdue' es derivado en UI (billingStatus.ts), no en DB.
  paid: 'success',
  overdue: 'danger',
  void: 'muted',
};

interface LineItemForm {
  description: string;
  qty: string;
  unitPriceDollars: string;
}

const EMPTY_LINE: LineItemForm = { description: '', qty: '1', unitPriceDollars: '' };

export interface EstimateDetailProps {
  id: number | null;
  accounts: BillingAccount[];
  /**
   * Only needed to CREATE a new estimate (id === null) — there's no loaded
   * estimate yet to read them from. When viewing/editing an existing
   * estimate (id !== null), contactId/projectId are read from the loaded
   * estimate itself, so these props aren't required from that context (e.g.
   * the standalone billing module, which doesn't have a "current project").
   */
  projectId?: number;
  contactId?: number;
}

// Body-only — no title, status pill, or header chrome. Those differ by
// caller context (the module's Reports-style toolbar header in
// BillingPage.tsx vs. the project tab's DetailSection header in
// EstimatesSection.tsx) and are rendered by each caller around this.
export function EstimateDetail({ id, projectId, contactId, accounts }: EstimateDetailProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: estimate, isLoading } = useEstimate(id ?? undefined);
  const createEstimate = useCreateEstimate();
  const updateEstimate = useUpdateEstimate();
  // Sentinel (-1) when id is null — never matches a real invoice, avoids an
  // unconditional fetch-all-invoices call from a brand-new create form.
  const { data: convertedInvoicesData } = useInvoices({ estimateId: id ?? -1 });
  const convertedInvoice = convertedInvoicesData?.invoices[0];

  const [accountId, setAccountId] = useState<number | null>(accounts[0]?.id ?? null);
  const [linkedProjectId, setLinkedProjectId] = useState<number | null>(projectId ?? null);
  const [lines, setLines] = useState<LineItemForm[]>([{ ...EMPTY_LINE }]);
  const [taxPercent, setTaxPercent] = useState('0');
  const [notes, setNotes] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>('info');

  // id !== null implies `estimate` is loaded by the time this is read for a
  // save — the component returns a LoadingState below before that point.
  // id === null (create) has no loaded estimate, so contactId comes from
  // the prop there (matching the existing account/create pattern).
  const resolvedContactId = estimate ? estimate.contactId : contactId;
  const { data: projectsData } = useContactProjects(resolvedContactId);
  const contactProjects = projectsData?.items ?? [];

  useEffect(() => {
    if (!estimate) return;
    setAccountId(estimate.accountId);
    setLinkedProjectId(estimate.projectId);
    const items = JSON.parse(estimate.lineItemsJson) as EstimateLineItem[];
    setLines(
      items.length
        ? items.map(li => ({ description: li.description, qty: String(li.qty), unitPriceDollars: (li.unitPrice / 100).toFixed(2) }))
        : [{ ...EMPTY_LINE }],
    );
    setTaxPercent((estimate.taxRate / 100).toString());
    setNotes(estimate.notes ?? '');
    setValidUntil(estimate.validUntil ?? '');
  }, [estimate]);

  const isDraft = !estimate || estimate.status === 'draft';
  const account = accounts.find(a => a.id === accountId);
  const currency = account?.currency ?? 'usd';
  const preview = previewTotals(lines, taxPercent);

  function updateLine(idx: number, patch: Partial<LineItemForm>) {
    setLines(ls => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    setSaved(false);
  }

  function addLine() {
    setLines(ls => [...ls, { ...EMPTY_LINE }]);
  }

  function removeLine(idx: number) {
    setLines(ls => (ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls));
  }

  function handleSave() {
    if (!accountId) return;
    if (resolvedContactId === undefined) return;
    const payload = {
      accountId,
      contactId: resolvedContactId,
      projectId: linkedProjectId,
      lineItems: lines
        .filter(l => l.description.trim())
        .map(l => ({
          description: l.description.trim(),
          qty: Math.max(0, Math.trunc(Number(l.qty) || 0)),
          unitPrice: Math.max(0, Math.round((Number(l.unitPriceDollars) || 0) * 100)),
        })),
      taxRate: Math.max(0, Math.round((Number(taxPercent) || 0) * 100)),
      notes: notes.trim() || null,
      validUntil: validUntil || null,
    };
    if (id === null) {
      createEstimate.mutate(payload, { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); } });
    } else {
      updateEstimate.mutate({ id, ...payload }, { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); } });
    }
  }

  if (id !== null && isLoading) return <LoadingState size="sm" />;

  const isPending = createEstimate.isPending || updateEstimate.isPending;

  return (
    <>
      {id !== null && (
        <div
          className="shrink-0"
          style={{
            height: 'var(--evo-tab-row-h)',
            display: 'flex',
            alignItems: 'flex-end',
            marginBottom: 'var(--evo-space-4)',
            borderBottom: '1px solid var(--evo-stroke)',
          }}
        >
          <Tabs<DetailTab>
            tabs={[
              { value: 'info', label: t('billing.estimates.tabs.info') },
              { value: 'outreach', label: t('billing.estimates.tabs.outreach') },
            ]}
            value={activeTab}
            onChange={setActiveTab}
            size="sm"
            noBorder
          />
        </div>
      )}

      {activeTab === 'outreach' && id !== null && estimate && (
        <EstimateOutreachTab estimate={estimate} />
      )}

      {(activeTab === 'info' || id === null) && (
      <>
      {estimate?.status === 'converted' && convertedInvoice && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--evo-space-2)', padding: 'var(--evo-space-3)', marginBottom: 'var(--evo-space-4)', background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)' }}>
          <span className="text-sm" style={{ color: 'var(--evo-fg-2)' }}>
            {t('billing.facturacion.convertedInfo', { number: convertedInvoice.number ?? `#${convertedInvoice.id}` })}
          </span>
          <Button size="sm" variant="outline" tone="cyan" onClick={() => navigate(`/facturacion/invoice/${convertedInvoice.id}`)}>
            {t('billing.facturacion.viewInvoice')}
          </Button>
        </div>
      )}

      {!isDraft && (
        <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.estimates.readOnlyHint')}</p>
      )}

      <Field label={t('billing.estimates.account')}>
        {accounts.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.estimates.noAccounts')}</p>
        ) : (
          <Dropdown<number>
            value={accountId ?? accounts[0]!.id}
            options={accounts.map(a => ({ value: a.id, label: `${a.name} (${a.currency.toUpperCase()})` }))}
            onChange={v => { setAccountId(v); setSaved(false); }}
            size="sm"
            disabled={!isDraft}
          />
        )}
      </Field>

      <Field label={t('billing.facturacion.project')}>
        <Select<number>
          value={linkedProjectId}
          onChange={setLinkedProjectId}
          options={contactProjects.map(p => ({ value: p.id, label: p.name }))}
          placeholder={contactProjects.length === 0 ? t('billing.facturacion.projectNone') : t('billing.facturacion.projectPlaceholder')}
          clearable
          disabled={!isDraft}
          size="sm"
          fullWidth
        />
      </Field>

      <Field label={t('billing.estimates.lineItems')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px 120px 32px', gap: 'var(--evo-space-2)', fontSize: '11px', color: 'var(--evo-fg-4)' }}>
            <span>{t('billing.estimates.description')}</span>
            <span>{t('billing.estimates.qty')}</span>
            <span>{t('billing.estimates.unitPrice')}</span>
            <span>{t('billing.estimates.amount')}</span>
            <span />
          </div>
          {lines.map((line, idx) => {
            const amount = Math.max(0, Math.trunc(Number(line.qty) || 0)) * Math.max(0, Math.round((Number(line.unitPriceDollars) || 0) * 100));
            return (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px 120px 32px', gap: 'var(--evo-space-2)', alignItems: 'center' }}>
                <Input value={line.description} onChange={v => updateLine(idx, { description: v })} size="sm" fullWidth disabled={!isDraft} />
                <Input type="number" value={line.qty} onChange={v => updateLine(idx, { qty: v })} size="sm" fullWidth disabled={!isDraft} />
                <Input type="number" value={line.unitPriceDollars} onChange={v => updateLine(idx, { unitPriceDollars: v })} size="sm" fullWidth disabled={!isDraft} />
                <span className="text-sm" style={{ color: 'var(--evo-fg-2)' }}>{formatMoney(amount, currency)}</span>
                {isDraft && (
                  <IconButton icon={<Icon name="close" size={14} />} size="sm" tone="muted" onClick={() => removeLine(idx)} ariaLabel={t('billing.estimates.removeLine')} />
                )}
              </div>
            );
          })}
          {isDraft && (
            <Button variant="outline" tone="muted" size="sm" onClick={addLine}>
              {t('billing.estimates.addLine')}
            </Button>
          )}
        </div>
      </Field>

      <Field label={t('billing.estimates.taxRatePercent')}>
        <Input type="number" value={taxPercent} onChange={setTaxPercent} size="sm" disabled={!isDraft} />
      </Field>

      <Field label={t('billing.estimates.notes')}>
        <Textarea value={notes} onChange={setNotes} rows={2} fullWidth disabled={!isDraft} />
      </Field>

      <Field label={t('billing.estimates.validUntil')}>
        <Input type="date" value={validUntil} onChange={setValidUntil} size="sm" disabled={!isDraft} />
      </Field>

      <KVGrid columns={2}>
        <KVRow label={t('billing.estimates.subtotal')} value={formatMoney(preview.subtotal, currency)} />
        <KVRow label={t('billing.estimates.tax')} value={formatMoney(preview.taxAmount, currency)} />
        <KVRow label={t('billing.estimates.total')} value={formatMoney(preview.total, currency)} last />
      </KVGrid>

      {isDraft && (
        <div style={{ marginTop: 'var(--evo-space-4)' }}>
          <Button
            tone="cyan"
            variant={saved ? 'outline' : 'solid'}
            size="sm"
            loading={isPending}
            disabled={!accountId}
            onClick={handleSave}
          >
            {isPending ? t('billing.estimates.saving') : saved ? t('billing.estimates.saved') : id === null ? t('billing.estimates.createBtn') : t('billing.estimates.save')}
          </Button>
        </div>
      )}
      </>
      )}
    </>
  );
}
